import { cn } from '@/lib/utils/cn.js';
import type { ReactNode } from 'react';

export type BadgeTone = 'success' | 'danger' | 'warning' | 'info' | 'muted' | 'primary';

export interface BadgeProps {
  tone?: BadgeTone;
  solid?: boolean;
  className?: string;
  children: ReactNode;
}

const TONE_MAP: Record<BadgeTone, { solid: string; soft: string }> = {
  success: {
    solid: 'bg-[var(--success)] text-[#06231b]',
    soft:  'bg-[var(--success-bg)] text-[var(--success)]',
  },
  danger: {
    solid: 'bg-[var(--danger)] text-white',
    soft:  'bg-[var(--danger-bg)] text-[var(--danger)]',
  },
  warning: {
    solid: 'bg-[var(--warning)] text-[#1f1300]',
    soft:  'bg-[var(--warning-bg)] text-[var(--warning)]',
  },
  info: {
    solid: 'bg-[var(--info)] text-white',
    soft:  'bg-[var(--info-bg)] text-[var(--info)]',
  },
  muted: {
    solid: 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]',
    soft:  'bg-[var(--bg-elevated)] text-[var(--text-secondary)]',
  },
  primary: {
    solid: 'bg-[var(--primary)] text-[var(--primary-on)]',
    soft:  'bg-[var(--primary)] text-[var(--primary-on)]',
  },
};

export function Badge({ tone = 'info', solid = false, className, children }: BadgeProps) {
  const toneClass = solid ? TONE_MAP[tone].solid : TONE_MAP[tone].soft;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-[.03em]',
        toneClass,
        className,
      )}
    >
      {children}
    </span>
  );
}
